import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Link from '../common/Link';
import {
  Trophy,
  Home,
  Users,
  LogOut,
  Crown,
  Bell,
  User,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Gamepad2,
  Swords,
  Shield,
  Calendar,
  Plus
} from '../common/Icons';
import { useAuth } from '../../context/AuthContext';
import { useTeams } from '../../context/TeamContext';
import { useChatHub } from '../../context/ChatHubContext';
import { useTranslation } from 'react-i18next';
import UserAvatar from '../common/UserAvatar';
import LanguageSwitcher from '../common/LanguageSwitcher';
import ChatManager from '../common/ChatManager';
import TournamentNotifications from '../features/tournaments/TournamentNotifications';
import PlatformSetupModal from '../features/onboarding/PlatformSetupModal';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import AnnouncementBar from "../common/AnnouncementBar";
import useLFSStore from '../../state/lfsStore';
import useUnscheduledMatchCount from '../../hooks/useUnscheduledMatchCount';
import useScheduleRequests from '../../hooks/useScheduleRequests';
import useTournamentPendingSubmissions from '../../hooks/useTournamentPendingSubmissions';
import CounterProposalModal from '../features/tournaments/CounterProposalModal';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { user, logout, signInWithDiscord } = useAuth();
  const { getUserInvites, respondToInvitation, loading, userTeams } = useTeams();
  const { t } = useTranslation();
  const unscheduledMatchCount = useUnscheduledMatchCount();
  const {
    scheduleRequests, scheduleApprovals, scheduleUpdates, scheduleCount,
    reload: reloadScheduleRequests, respond: respondToScheduleRequest,
    approveApproval, rejectApproval, dismissUpdate, formatDateTime: formatScheduleDateTime
  } = useScheduleRequests();
  const { submissions: tournamentSubmissions, count: tournamentSubmissionCount } = useTournamentPendingSubmissions();
  const [selectedScheduleRequest, setSelectedScheduleRequest] = useState(null);
  const [showCounterProposalModal, setShowCounterProposalModal] = useState(false);
  const { unreadCount, openChatHub } = useChatHub();
  const { totalLFSNotifications, setCurrentUser, loadRequests, incomingRequests, unreadScrimChats, scrimNotifications, requestNotifications, markRequestNotificationRead, scrimCancellationNotifications, markScrimNotificationRead } = useLFSStore();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showTournamentsDropdown, setShowTournamentsDropdown] = useState(false);
  const [showMobileTournamentsDropdown, setShowMobileTournamentsDropdown] = useState(false);
  const [showTeamsDropdown, setShowTeamsDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Trigger Discord OAuth directly from the navbar (no dedicated /login page)
  const handleSignIn = () => {
    signInWithDiscord?.();
  };


  
  // Check for pending join requests for owned teams
  useEffect(() => {
    const checkPendingJoinRequests = async () => {
      if (!user || !userTeams.length) {
        setPendingJoinRequests(0);
        return;
      }
      
      const ownedTeams = userTeams.filter(team => team.role === 'owner');
      if (ownedTeams.length === 0) {
        setPendingJoinRequests(0);
        return;
      }
      
      try {
        const teamIds = ownedTeams.map(team => team.id);
        const { data, error } = await supabase
          .from('team_join_requests')
          .select('id')
          .in('team_id', teamIds)
          .eq('status', 'pending');
        
        if (error) {
          console.error('Error checking join requests:', error);
          return;
        }
        
        setPendingJoinRequests(data?.length || 0);
      } catch (error) {
        console.error('Error checking join requests:', error);
      }
    };
    
    checkPendingJoinRequests();
    
    // Check every 30 seconds for new requests
    const interval = setInterval(checkPendingJoinRequests, 30000);
    
    return () => clearInterval(interval);
  }, [user, userTeams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTournamentsDropdown && !event.target.closest('.tournaments-dropdown')) {
        setShowTournamentsDropdown(false);
      }
      if (showMobileTournamentsDropdown && !event.target.closest('.mobile-tournaments-dropdown')) {
        setShowMobileTournamentsDropdown(false);
      }
      if (showTeamsDropdown && !event.target.closest('.teams-dropdown')) {
        setShowTeamsDropdown(false);
      }
      if (showUserMenu && !event.target.closest('.user-menu')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTournamentsDropdown, showMobileTournamentsDropdown, showTeamsDropdown, showUserMenu]);

  // Initialize LFS notifications when user is available
  useEffect(() => {
    if (user) {
      setCurrentUser(user.id)
      loadRequests()
    }
  }, [user, setCurrentUser, loadRequests])

  const userInvites = getUserInvites();
  const [pendingJoinRequests, setPendingJoinRequests] = useState(0);

  // Gaming-profile-incomplete notification (platform + gamer tag + position missing)
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [showGamingSetup, setShowGamingSetup] = useState(false);
  useEffect(() => {
    if (!user?.id) { setProfileIncomplete(false); return; }
    if (localStorage.getItem(`profile_setup_dismissed_${user.id}`) === 'true') { setProfileIncomplete(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('users')
          .select('platform, gamer_tag, position')
          .eq('id', user.id)
          .single();
        if (cancelled || !data) return;
        setProfileIncomplete(!data.platform || !data.gamer_tag || !data.position);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id, showGamingSetup]);

  const dismissGamingProfile = () => {
    if (user?.id) localStorage.setItem(`profile_setup_dismissed_${user.id}`, 'true');
    setProfileIncomplete(false);
  };

  const renderGamingProfileCard = () => (
    <div className="p-4 border-b border-white/10 hover:bg-white/[0.06] bg-[#31C590]/[0.06]">
      <div className="flex items-start gap-2">
        <Gamepad2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#23F8B5]" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-white text-sm">{t('notifications.gamingProfile.title', { defaultValue: 'Complete your gaming profile' })}</p>
          <p className="text-xs text-gray-300 mt-0.5">{t('notifications.gamingProfile.message', { defaultValue: 'Connect your gaming account to unlock rank tracking and tournaments.' })}</p>
          <button
            onClick={() => { setShowNotifications(false); setShowGamingSetup(true); }}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 transition-colors"
          >
            {t('notifications.gamingProfile.action', { defaultValue: 'Setup profile' })}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={dismissGamingProfile}
          className="flex-shrink-0 text-gray-400 hover:text-white transition-colors p-1"
          aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );

  const hasNotifications = userInvites.length > 0 || pendingJoinRequests > 0 || totalLFSNotifications > 0 || scheduleCount > 0 || tournamentSubmissionCount > 0 || profileIncomplete;

  // Schedule requests are urgent/deadline-driven → surface them with an amber accent
  const handleCounterProposalSubmit = async (requestId, response, responseData) => {
    await respondToScheduleRequest(requestId, response, responseData);
    setShowCounterProposalModal(false);
    setSelectedScheduleRequest(null);
  };

  const renderScheduleRequestCards = () => scheduleRequests.map((request) => {
    const isCounter = request.status === 'counter_proposed';
    return (
      <div key={request.request_id} className="p-4 border-b border-white/10 hover:bg-white/[0.06] bg-amber-500/[0.06]">
        <div className="flex items-start gap-2 mb-2">
          <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-white text-sm">
              {isCounter ? 'Schedule Counter-Proposal' : 'Schedule Request'}
            </p>
            <p className="text-xs text-gray-300 mt-0.5">
              <span className="font-medium text-amber-300">{isCounter ? request.opponent_team_name : request.proposer_team_name}</span>{' '}
              proposed a time for <span className="text-white">{request.tournament_name}</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {formatScheduleDateTime(
                isCounter ? request.counter_proposed_date : request.proposed_date,
                isCounter ? request.counter_proposed_time : request.proposed_time,
                request.timezone
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => respondToScheduleRequest(request.request_id, 'accept')}
            className="flex-1 bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 px-2 py-1 rounded-md text-xs font-semibold transition-colors"
          >
            {t('common.accept', { defaultValue: 'Accept' })}
          </button>
          <button
            onClick={() => respondToScheduleRequest(request.request_id, 'reject', { responseNotes: 'Schedule not suitable' })}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-md text-xs font-semibold transition-colors"
          >
            {t('common.reject', { defaultValue: 'Reject' })}
          </button>
          <button
            onClick={() => { setSelectedScheduleRequest(request); setShowCounterProposalModal(true); setShowNotifications(false); }}
            className="flex-1 bg-amber-500 hover:bg-amber-400 text-zinc-900 px-2 py-1 rounded-md text-xs font-semibold transition-colors"
          >
            {t('tournaments.scheduling.counter', { defaultValue: 'Counter' })}
          </button>
        </div>
      </div>
    );
  });

  // Admin: schedule requests needing tournament-owner approval
  // Pending tournament result submissions awaiting the owner's review (mirrors the floating widget)
  const renderTournamentSubmissionCards = () => tournamentSubmissions.map((s) => {
    const data = s.team1_submission_data || s.team2_submission_data || {};
    const t1 = data.team1_score ?? 0;
    const t2 = data.team2_score ?? 0;
    const statusLabel = s.submission_status === 'partial'
      ? t('notifications.awaitingOpponent', { defaultValue: 'Awaiting opponent submission' })
      : s.submission_status === 'conflicted'
        ? t('notifications.resultsConflict', { defaultValue: 'Results conflict — needs review' })
        : t('notifications.needsReview', { defaultValue: 'Needs your review' });
    return (
      <div key={s.id} className="p-4 border-b border-white/10 hover:bg-white/[0.06] bg-amber-500/[0.06]">
        <div className="flex items-start gap-2">
          <Trophy className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-white text-sm">{t('notifications.resultsPendingApproval', { defaultValue: 'Results pending approval' })}</p>
            {s.tournament_name && <p className="text-xs text-gray-300 mt-0.5 truncate">{s.tournament_name}</p>}
            <p className="text-xs text-gray-400 mt-1 truncate">
              {s.team1_name} vs {s.team2_name} · {t1}–{t2}
            </p>
            <p className="text-[11px] text-amber-300/90 mt-0.5">{statusLabel}</p>
          </div>
        </div>
        <div className="mt-2">
          <button
            onClick={() => { setShowNotifications(false); window.dispatchEvent(new CustomEvent('openTournamentSubmissionReview', { detail: { matchId: s.match_id } })); }}
            className="w-full bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 px-2 py-1 rounded-md text-xs font-semibold transition-colors"
          >
            {t('notifications.reviewSubmission', { defaultValue: 'Review Submission' })}
          </button>
        </div>
      </div>
    );
  });

  const renderScheduleApprovalCards = () => scheduleApprovals.map((notification) => {
    const req = notification.match_schedule_requests;
    return (
      <div key={notification.id} className="p-4 border-b border-white/10 hover:bg-white/[0.06] bg-amber-500/[0.06]">
        <div className="flex items-start gap-2 mb-2">
          <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-white text-sm">{notification.title}</p>
            {notification.message && <p className="text-xs text-gray-300 mt-0.5">{notification.message}</p>}
            {req && (
              <p className="text-xs text-gray-400 mt-1">
                {req.matches?.team1?.name} vs {req.matches?.team2?.name}
                {req.proposed_date && <> · {formatScheduleDateTime(req.proposed_date, req.proposed_time, req.timezone)}</>}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => approveApproval(notification)}
            className="flex-1 bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 px-2 py-1 rounded-md text-xs font-semibold transition-colors"
          >
            {t('common.approve', { defaultValue: 'Approve' })}
          </button>
          <button
            onClick={() => rejectApproval(notification)}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-md text-xs font-semibold transition-colors"
          >
            {t('common.reject', { defaultValue: 'Reject' })}
          </button>
        </div>
      </div>
    );
  });

  // Informational schedule status updates (accepted / rejected) — dismissible
  const renderScheduleUpdateCards = () => scheduleUpdates.map((notification) => {
    const req = notification.match_schedule_requests;
    return (
      <div key={notification.id} className="p-4 border-b border-white/10 hover:bg-white/[0.06]">
        <div className="flex items-start gap-2">
          <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#23F8B5]" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-white text-sm">{notification.title}</p>
            {notification.message && <p className="text-xs text-gray-300 mt-0.5">{notification.message}</p>}
            {req && (
              <p className="text-xs text-gray-400 mt-1">
                {req.matches?.team1?.name} vs {req.matches?.team2?.name}
                {req.proposed_date && <> · {formatScheduleDateTime(req.proposed_date, req.proposed_time, req.timezone)}</>}
              </p>
            )}
          </div>
          <button
            onClick={() => dismissUpdate(notification)}
            className="flex-shrink-0 text-gray-400 hover:text-white transition-colors p-1"
            aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleAcceptInvite = async (inviteId) => {
    const result = await respondToInvitation(inviteId, 'accepted');
    if (result.success) {
      toast.success(t('notifications.invitationAccepted'));
      setShowNotifications(false);
    } else {
      toast.error(result.error);
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    const result = await respondToInvitation(inviteId, 'declined');
    if (result.success) {
      toast.success(t('notifications.invitationDeclined'));
      setShowNotifications(false);
    } else {
      toast.error(result.error);
    }
  };

  const navigationItems = [
    { key: 'tournaments', label: t('navigation.tournaments'), icon: Trophy, path: '/tournaments', color: 'purple', requiresAuth: false },
    { key: 'leaderboards', label: t('navigation.leaderboards').toUpperCase(), icon: TrendingUp, path: '/leaderboards', color: 'purple', requiresAuth: false },
    { key: 'lfs', label: 'SCRIMS', icon: Swords, path: '/lfs/schedule', color: 'blue', requiresAuth: true },
    { key: 'teams', label: t('navigation.teams'), icon: Users, path: '/teams', color: 'green', requiresAuth: false },
    { key: 'players', label: t('navigation.players'), icon: User, path: '/players', color: 'indigo', requiresAuth: false }
  ];

  const mobileNavigationItems = [
    { key: 'tournaments', label: t('navigation.tournaments').toUpperCase(), icon: Trophy, path: '/tournaments', color: 'purple', requiresAuth: false },
    { key: 'leaderboards', label: t('navigation.leaderboards').toUpperCase(), icon: TrendingUp, path: '/leaderboards', color: 'purple', requiresAuth: false },
    { key: 'lfs', label: 'LFS', icon: Swords, path: '/lfs/schedule', color: 'blue', requiresAuth: true },
    { key: 'teams', label: t('navigation.teams').toUpperCase(), icon: Users, path: '/teams', color: 'green', requiresAuth: false },
    { key: 'players', label: t('navigation.players').toUpperCase(), icon: User, path: '/players', color: 'indigo', requiresAuth: false }
  ];

  const getActiveTab = () => {
    const path = location.pathname;
    
    if (path.startsWith('/tournament')) return 'tournaments';
    if (path === '/') return null; // Home accessed via logo
    if (path === '/calendar') return 'calendar';
    if (path === '/tournaments') {
      const urlParams = new URLSearchParams(window.location.search);
      const tab = urlParams.get('tab');
      if (tab === 'upcoming') return 'upcoming-tournaments';
      if (tab === 'live') return 'live-tournaments';
      if (tab === 'completed') return 'completed-tournaments';
      return 'upcoming-tournaments'; // default tab
    }
    if (path.startsWith('/lfs')) return 'lfs';
    if (path === '/scrim-matches') return 'scrim-matches';
    if (path === '/teams') return 'teams';
    if (path === '/players') return 'players';
    if (path === '/stats') return 'stats';
    if (path === '/leaderboards') return 'leaderboards';
    if (path === '/profile' || path.startsWith('/profile/')) return 'profile';
    if (path === '/') return null; // Home/Dashboard accessed via logo
    return null;
  };

  const activeTab = getActiveTab();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-zinc-900 dark:to-zinc-800 text-white transition-colors flex flex-col">
      {/* Navigation */}
      <div className="sticky top-0 z-50">
        <div>
          {/* Single Line Header — flat dark bg with an edge-faded teal bottom line (goalsverse-style) */}
          <div className="relative backdrop-blur-md"
               style={{background: 'rgba(18, 21, 30, 0.82)'}}>
            {/* Bottom accent line: bright in the middle, fades out at both edges */}
            <div aria-hidden className="pointer-events-none absolute bottom-0 inset-x-0 h-px z-20"
                 style={{background: 'linear-gradient(to right, transparent 0%, rgba(49,197,144,0.85) 50%, transparent 100%)'}} />
            <div aria-hidden className="pointer-events-none absolute bottom-0 inset-x-0 h-[6px] z-10 blur-[3px]"
                 style={{background: 'linear-gradient(to right, transparent 8%, rgba(49,197,144,0.35) 50%, transparent 92%)'}} />
            <div className="relative z-30 max-w-screen-2xl mx-auto px-3 sm:px-4">
              <div className="relative flex items-center h-16 sm:h-16">
                {/* Left: Logo */}
                <div className="flex items-center flex-shrink-0 mr-3 sm:mr-6">
                  <Link
                      to="/"
                      className="transition-all duration-500 cursor-pointer group no-underline"
                      aria-label="Go to Home"
                  >
                    <img
                        src="/logoirl.webp"
                        alt="VRG Logo"
                        className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 object-contain group-hover:scale-110 transition-all duration-500 group-hover:drop-shadow-[0_0_20px_rgba(255,255,255,0.8)]"
                    />
                  </Link>
                </div>

                {/* Center: Navigation Items — goalsverse-style grouped pill bar (absolutely centered) */}
                <div
                    className="hidden lg:flex items-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <div
                      className="flex items-center gap-0.5 xl:gap-1 p-1 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                    {/* Home / HUB pill */}
                    {user && (
                        <Link
                            to="/"
                            className={`flex items-center justify-center gap-1.5 px-3 xl:px-3.5 py-2 rounded-full font-semibold text-xs xl:text-[13px] whitespace-nowrap no-underline transition-all duration-300 ${
                                location.pathname === '/'
                                    ? 'bg-[#31C590]/20 text-[#23F8B5] shadow-[inset_0_0_0_1px_rgba(35,248,181,0.35)]'
                                    : 'text-white/65 hover:text-white hover:bg-white/[0.07]'
                            }`}
                            title={t('navigation.home',
                                {defaultValue: 'Home'})}
                        >
                          <Home className="w-3.5 h-3.5"/>
                          <span className="hidden xl:inline">HUB</span>
                        </Link>
                    )}

                    {navigationItems.map(item => {
                      const Icon = item.icon;
                      const isActive = activeTab === item.key || (item.dropdown
                          && item.dropdown.some(
                              dropdownItem => dropdownItem.key === activeTab));
                      const isDisabled = !user && item.requiresAuth;

                      const pillClass = (active) => `flex items-center justify-center gap-1.5 px-3 xl:px-3.5 py-2 rounded-full font-semibold text-xs xl:text-[13px] whitespace-nowrap no-underline transition-all duration-300 ${
                          active
                              ? 'bg-[#31C590]/20 text-[#23F8B5] shadow-[inset_0_0_0_1px_rgba(35,248,181,0.35)]'
                              : isDisabled
                                  ? 'text-white/35 cursor-pointer hover:text-white/55'
                                  : 'text-white/65 hover:text-white hover:bg-white/[0.07]'
                      }`;

                      if (item.dropdown) {
                        const isDropdownOpen = showTournamentsDropdown;
                        const setDropdownOpen = setShowTournamentsDropdown;
                        const dropdownClass = 'tournaments-dropdown';

                        return (
                            <div key={item.key}
                                 className={`relative ${dropdownClass} group`}>
                              <button
                                  onClick={() => {
                                    if (isDisabled) {
                                      handleSignIn();
                                    } else {
                                      setDropdownOpen(!isDropdownOpen);
                                    }
                                  }}
                                  className={pillClass(isActive)}
                                  title={isDisabled ? t('auth.signInRequired')
                                      : ''}
                              >
                                <Icon className={`w-3.5 h-3.5 ${isDisabled
                                    ? 'opacity-50' : ''}`}/>
                                <span className={`${isDisabled ? 'opacity-50'
                                    : ''} hidden xl:inline`}>{item.label}</span>
                                <ChevronDown
                                    className={`w-3 h-3 transition-transform ${isDropdownOpen
                                        ? 'rotate-180' : ''}`}/>
                              </button>

                              {isDropdownOpen && !isDisabled && (
                                  <div
                                      className="absolute top-full left-0 mt-2 bg-[#12151e] rounded-xl shadow-xl border border-white/10 py-2 min-w-[220px] z-60 group-focus-within:block group-hover:block">

                                    {item.dropdown.map(dropdownItem => {
                                      const DropdownIcon = dropdownItem.icon;
                                      return (
                                          <Link
                                              key={dropdownItem.key}
                                              to={dropdownItem.path}
                                              onClick={() => {
                                                setDropdownOpen(false);
                                              }}
                                              className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium transition-all duration-200 text-left focus:outline-none focus:bg-gray-100 dark:focus:bg-white/[0.08] no-underline ${
                                                  activeTab === dropdownItem.key
                                                      ? 'bg-[#e6f9f3] dark:bg-[#174e42] text-[#38c494] dark:text-[#38c494]'
                                                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.06]'
                                              }`}
                                          >
                                            <DropdownIcon className="w-4 h-4"/>
                                            <span>{dropdownItem.label}</span>
                                          </Link>
                                      );
                                    })}
                                  </div>
                              )}
                            </div>
                        );
                      }

                      // Regular navigation item
                      return (
                          <Link
                              key={item.key}
                              to={isDisabled ? '#' : item.path}
                              onClick={(e) => { if (isDisabled) { e.preventDefault(); handleSignIn(); } }}
                              className={pillClass(isActive)}
                              title={isDisabled ? t('auth.signInRequired') : ''}
                          >
                            <Icon
                                className={`w-3.5 h-3.5 ${isDisabled ? 'opacity-50'
                                    : ''}`}/>
                            <span className={`${isDisabled ? 'opacity-50'
                                : ''} hidden xl:inline`}>{item.label}</span>
                          </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Right: User Actions */}
                <div
                    className="flex items-center gap-1 sm:gap-2 md:gap-4 ml-auto">
                  {user ? (
                      <>
                        {/* Calendar (sits to the left of Your Teams via order) */}
                        <Link
                            to="/calendar"
                            className={`order-0 relative hidden sm:flex items-center justify-center p-2 rounded-lg touch-manipulation transition-colors ${
                              unscheduledMatchCount > 0
                                ? 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25'
                                : 'bg-white/10 text-white/80 hover:text-white hover:bg-white/20 dark:hover:bg-white/30'
                            }`}
                            title={t('navigation.calendar', { defaultValue: 'Calendar' })}
                            aria-label={t('navigation.calendar', { defaultValue: 'Calendar' })}
                        >
                          <Calendar className="w-4 h-4"/>
                          {unscheduledMatchCount > 0 && (
                            <span className="absolute -bottom-1 -right-1 min-w-[1.05rem] h-[1.05rem] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-amber-500 text-zinc-900 ring-2 ring-[#191c23]">
                              {unscheduledMatchCount > 9 ? '9+' : unscheduledMatchCount}
                            </span>
                          )}
                        </Link>

                        {/* Notifications (sits to the right of Your Teams via order) */}
                        <div className="relative order-2">
                          <button
                              onClick={() => {
                                if (!showNotifications) reloadScheduleRequests();
                                setShowNotifications(!showNotifications);
                              }}
                              className={`relative p-2 rounded-lg touch-manipulation transition-colors ${
                                hasNotifications
                                  ? 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25'
                                  : 'text-white/80 hover:text-white bg-white/10 hover:bg-white/20 dark:hover:bg-white/30'
                              }`}
                          >
                            <Bell className="w-4 h-4"/>
                            {hasNotifications && (
                                <span className="absolute -bottom-1 -right-1 min-w-[1.05rem] h-[1.05rem] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-amber-500 text-zinc-900 ring-2 ring-[#191c23]">
                                  {(() => { const c = userInvites.length + pendingJoinRequests + totalLFSNotifications + scheduleCount + tournamentSubmissionCount + (profileIncomplete ? 1 : 0); return c > 9 ? '9+' : c; })()}
                                </span>
                            )}
                          </button>

                          {showNotifications && (
                              <>
                                {/* Mobile Notifications - Centered Modal */}
                                <div className="sm:hidden fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" style={{ paddingTop: '250px' }}>
                                  <div className="w-full max-w-sm bg-[#12151e] rounded-xl shadow-2xl border border-white/10 max-h-[70vh] overflow-hidden">
                                    <div className="p-4 border-b border-gray-100 dark:border-white/10 flex items-center justify-between">
                                      <h3 className="font-bold text-white">{t('common.notifications')}</h3>
                                      <button
                                        onClick={() => setShowNotifications(false)}
                                        className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-gray-600 transition-colors"
                                      >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                    <div className="max-h-64 overflow-y-auto">
                                      {/* Schedule (urgent) */}
                                      {scheduleApprovals.length > 0 && renderScheduleApprovalCards()}
                                      {scheduleRequests.length > 0 && renderScheduleRequestCards()}
                                      {scheduleUpdates.length > 0 && renderScheduleUpdateCards()}
                                      {tournamentSubmissions.length > 0 && renderTournamentSubmissionCards()}

                                      {/* Gaming profile setup */}
                                      {profileIncomplete && renderGamingProfileCard()}

                                      {/* Team Invitations */}
                                      {userInvites.length > 0 && (
                                          userInvites.map(invite => (
                                              <div key={invite.id}
                                                   className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                                <div
                                                    className="flex items-center justify-between">
                                                  <div className="flex-1 mr-4">
                                                    <p className="font-semibold text-white">{t(
                                                        'notifications.teamInvitation')}</p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                                      {t('notifications.invitedToJoin')}
                                                      <span
                                                          className="font-medium">{invite.team?.name
                                                          || t(
                                                              'notifications.unknownTeam')}</span>
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t(
                                                        'notifications.by')} {invite.invitedBy}</p>
                                                    {invite.message && (
                                                        <div
                                                            className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border-l-2 border-blue-300">
                                                          <p className="text-xs text-gray-700 dark:text-gray-300 italic">"{invite.message}"</p>
                                                        </div>
                                                    )}
                                                  </div>
                                                  <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleAcceptInvite(
                                                            invite.id)}
                                                        disabled={loading}
                                                        className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                      {t('common.accept')}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeclineInvite(
                                                            invite.id)}
                                                        disabled={loading}
                                                        className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                      {t('common.decline')}
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                          ))
                                      )}

                                      {/* Join Requests Notification */}
                                      {pendingJoinRequests > 0 && (
                                          <div
                                              className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                            <div
                                                className="flex items-center justify-between">
                                              <div>
                                                <p className="font-semibold text-white">{t(
                                                    'notifications.joinRequests',
                                                    {defaultValue: 'Join Requests'})}</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                                  {pendingJoinRequests} {pendingJoinRequests
                                                === 1 ? t(
                                                        'notifications.playerWantsToJoin',
                                                        {defaultValue: 'player wants to join your team'})
                                                    : t('notifications.playersWantToJoin',
                                                        {defaultValue: 'players want to join your teams'})}
                                                </p>
                                              </div>
                                              <button
                                                  onClick={async () => {
                                                    setShowNotifications(false);

                                                    // Find the first team with pending join requests and navigate to it
                                                    const ownedTeams = userTeams.filter(
                                                        team => team.role
                                                            === 'owner');
                                                    if (ownedTeams.length > 0) {
                                                      try {
                                                        // Get the first team with pending requests
                                                        for (const team of
                                                            ownedTeams) {
                                                          const {
                                                            data,
                                                            error
                                                          } = await supabase
                                                          .from(
                                                              'team_join_requests')
                                                          .select('id')
                                                          .eq('team_id', team.id)
                                                          .eq('status', 'pending')
                                                          .limit(1);

                                                          if (!error && data
                                                              && data.length > 0) {
                                                            // Navigate to this team with manage parameter
                                                            navigate(
                                                                `/teams/${team.id}?manage=true`);
                                                            return;
                                                          }
                                                        }

                                                        // If no team with pending requests found, navigate to first owned team
                                                        navigate(
                                                            `/teams/${ownedTeams[0].id}?manage=true`);
                                                      } catch (error) {
                                                        console.error(
                                                            'Error finding team with pending requests:',
                                                            error);
                                                        // Fallback to teams page
                                                        navigate('/teams');
                                                      }
                                                    } else {
                                                      // Fallback to teams page
                                                      navigate('/teams');
                                                    }
                                                  }}
                                                  className="bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 px-3 py-1 rounded-md text-sm font-semibold transition-colors"
                                              >
                                                {t('teams.manage',
                                                    {defaultValue: 'Manage'})}
                                              </button>
                                            </div>
                                          </div>
                                      )}

                                      {/* LFS Scrim Requests */}
                                      {incomingRequests.filter(req => req.status === 'Proposed').length > 0 && (
                                          <div className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                            <div className="flex items-center justify-between">
                                              <div>
                                                <p className="font-semibold text-white">Scrim Requests</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                                  {incomingRequests.filter(req => req.status === 'Proposed').length} new scrim {incomingRequests.filter(req => req.status === 'Proposed').length === 1 ? 'request' : 'requests'}
                                                </p>
                                              </div>
                                              <button
                                                onClick={() => {
                                                  setShowNotifications(false)
                                                  navigate('/lfs/requests')
                                                }}
                                                className="bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 px-3 py-1 rounded-md text-sm font-semibold transition-colors"
                                              >
                                                View
                                              </button>
                                            </div>
                                          </div>
                                      )}

                                      {/* LFS Scrim Chat Notifications */}
                                      {unreadScrimChats > 0 && (
                                        Object.entries(scrimNotifications).map(([scrimId, notification]) => (
                                          <div key={scrimId} className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                            <div className="flex items-center justify-between">
                                              <div>
                                                <p className="font-semibold text-white">Scrim Chat</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                                  New messages vs {notification.opponentTeam}
                                                </p>
                                              </div>
                                              <button
                                                onClick={() => {
                                                  setShowNotifications(false)
                                                  navigate(`/lfs/scrims?openChat=${scrimId}`)
                                                }}
                                                className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 transition-colors"
                                              >
                                                View
                                              </button>
                                            </div>
                                          </div>
                                        ))
                                      )}

                                      {/* LFS Request Notifications (Accept/Decline/Cancel) - Mobile */}
                                      {requestNotifications && requestNotifications.length > 0 && (
                                        requestNotifications.map(notification => (
                                          <div key={notification.id} className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                            <div className="flex items-center justify-between">
                                              <div className="flex-1 mr-4">
                                                <p className="font-semibold text-white">
                                                  {notification.notification_type === 'request_accepted' && 'Request Accepted'}
                                                  {notification.notification_type === 'request_expired' && 'Request Expired'}
                                                  {notification.notification_type === 'request_cancelled' && 'Request Declined'}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                                  {notification.notification_type === 'request_accepted' && `${notification.to_team_name} accepted your scrim request`}
                                                  {notification.notification_type === 'request_expired' && `Your request to ${notification.to_team_name} has expired`}
                                                  {notification.notification_type === 'request_cancelled' && `${notification.to_team_name} declined your scrim request`}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                  {notification.platform} • {notification.region} • {notification.format}
                                                </p>
                                              </div>
                                              <button
                                                onClick={async () => {
                                                  // Determine team role for marking as read
                                                  const { data: { user } } = await supabase.auth.getUser()
                                                  if (!user) return

                                                  const { data: userTeams } = await supabase
                                                    .from('team_members')
                                                    .select('team_id')
                                                    .eq('user_id', user.id)

                                                  const teamIds = userTeams?.map(t => t.team_id) || []
                                                  const isFromTeam = teamIds.includes(notification.from_team_id)
                                                  const teamRole = isFromTeam ? 'from_team' : 'to_team'

                                                  await markRequestNotificationRead(notification.id, teamRole)

                                                  // Navigate to scrims page if request was accepted
                                                  if (notification.notification_type === 'request_accepted') {
                                                    navigate('/lfs/scrims')
                                                  }
                                                }}
                                                className={`px-3 py-1 rounded text-sm transition-colors ${
                                                  notification.notification_type === 'request_accepted'
                                                    ? 'bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900'
                                                  : 'bg-[#22262f] border border-white/10 text-gray-300 hover:bg-[#262b35]'
                                                }`}
                                              >
                                                {notification.notification_type === 'request_accepted' ? 'View Scrim' : 'Dismiss'}
                                              </button>
                                            </div>
                                          </div>
                                        ))
                                      )}

                                      {/* LFS Scrim Cancellation Notifications - Mobile */}
                                      {scrimCancellationNotifications && scrimCancellationNotifications.length > 0 && (
                                        scrimCancellationNotifications.map(notification => (
                                          <div key={notification.id} className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                            <div className="flex items-center justify-between">
                                              <div className="flex-1 mr-4">
                                                <p className="font-semibold text-white">
                                                  Scrim Cancelled
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                                  {notification.cancelled_by_team_id === notification.team_a_id
                                                    ? `${notification.team_a_name} cancelled your scheduled scrim`
                                                    : `${notification.team_b_name} cancelled your scheduled scrim`
                                                  }
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                  {notification.platform} • {notification.region} • {notification.format}
                                                </p>
                                              </div>
                                              <button
                                                onClick={async () => {
                                                  // Determine which team the user is on and mark as read
                                                  const { data: { user } } = await supabase.auth.getUser()
                                                  if (!user) return

                                                  const { data: userTeams } = await supabase
                                                    .from('team_members')
                                                    .select('team_id')
                                                    .eq('user_id', user.id)

                                                  const teamIds = userTeams?.map(t => t.team_id) || []
                                                  await markScrimNotificationRead(notification.id, teamIds)
                                                }}
                                                className="bg-[#22262f] border border-white/10 text-gray-300 px-3 py-1 rounded-md text-sm font-semibold hover:bg-[#262b35] transition-colors"
                                              >
                                                Dismiss
                                              </button>
                                            </div>
                                          </div>
                                        ))
                                      )}

                                      {userInvites.length === 0
                                          && pendingJoinRequests === 0 && totalLFSNotifications === 0 && scheduleCount === 0 && tournamentSubmissionCount === 0 && (
                                              <div
                                                  className="p-8 text-center text-gray-500 dark:text-gray-300">
                                                <Bell
                                                    className="w-8 h-8 mx-auto mb-2 opacity-50"/>
                                                <p>{t('common.noNotifications')}</p>
                                              </div>
                                          )}
                                    </div>
                                  </div>
                                </div>

                                {/* Desktop Notifications */}
                                <div className="hidden sm:block absolute right-0 mt-2 w-80 bg-[#12151e] rounded-xl shadow-2xl border border-white/10 z-50">
                                  <div className="p-4 border-b border-gray-100 dark:border-white/10">
                                    <h3 className="font-bold text-white">{t('common.notifications')}</h3>
                                  </div>
                                  <div className="max-h-64 overflow-y-auto">
                                    {/* Schedule (urgent) */}
                                    {scheduleApprovals.length > 0 && renderScheduleApprovalCards()}
                                    {scheduleRequests.length > 0 && renderScheduleRequestCards()}
                                    {scheduleUpdates.length > 0 && renderScheduleUpdateCards()}
                                    {tournamentSubmissions.length > 0 && renderTournamentSubmissionCards()}

                                    {/* Gaming profile setup */}
                                    {profileIncomplete && renderGamingProfileCard()}

                                    {/* Team Invitations */}
                                    {userInvites.length > 0 && (
                                        userInvites.map(invite => (
                                            <div key={invite.id}
                                                 className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                              <div
                                                  className="flex items-center justify-between">
                                                <div className="flex-1 mr-4">
                                                  <p className="font-semibold text-white">{t(
                                                      'notifications.teamInvitation')}</p>
                                                  <p className="text-sm text-gray-600 dark:text-gray-300">
                                                    {t('notifications.invitedToJoin')}
                                                    <span
                                                        className="font-medium">{invite.team?.name
                                                        || t(
                                                            'notifications.unknownTeam')}</span>
                                                  </p>
                                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t(
                                                      'notifications.by')} {invite.invitedBy}</p>
                                                  {invite.message && (
                                                      <div
                                                          className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border-l-2 border-blue-300">
                                                        <p className="text-xs text-gray-700 dark:text-gray-300 italic">"{invite.message}"</p>
                                                      </div>
                                                  )}
                                                </div>
                                                <div className="flex space-x-2">
                                                  <button
                                                      onClick={() => handleAcceptInvite(
                                                          invite.id)}
                                                      disabled={loading}
                                                      className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                  >
                                                    {t('common.accept')}
                                                  </button>
                                                  <button
                                                      onClick={() => handleDeclineInvite(
                                                          invite.id)}
                                                      disabled={loading}
                                                      className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                  >
                                                    {t('common.decline')}
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                        ))
                                    )}

                                    {/* Join Requests Notification */}
                                    {pendingJoinRequests > 0 && (
                                        <div
                                            className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                          <div
                                              className="flex items-center justify-between">
                                            <div>
                                              <p className="font-semibold text-white">{t(
                                                  'notifications.joinRequests',
                                                  {defaultValue: 'Join Requests'})}</p>
                                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                                {pendingJoinRequests} {pendingJoinRequests
                                              === 1 ? t(
                                                      'notifications.playerWantsToJoin',
                                                      {defaultValue: 'player wants to join your team'})
                                                  : t('notifications.playersWantToJoin',
                                                      {defaultValue: 'players want to join your teams'})}
                                              </p>
                                            </div>
                                            <button
                                                onClick={async () => {
                                                  setShowNotifications(false);

                                                  // Find the first team with pending join requests and navigate to it
                                                  const ownedTeams = userTeams.filter(
                                                      team => team.role
                                                          === 'owner');
                                                  if (ownedTeams.length > 0) {
                                                    try {
                                                      // Get the first team with pending requests
                                                      for (const team of
                                                          ownedTeams) {
                                                        const {
                                                          data,
                                                          error
                                                        } = await supabase
                                                        .from(
                                                            'team_join_requests')
                                                        .select('id')
                                                        .eq('team_id', team.id)
                                                        .eq('status', 'pending')
                                                        .limit(1);

                                                        if (!error && data
                                                            && data.length > 0) {
                                                          // Navigate to this team with manage parameter
                                                          navigate(
                                                              `/teams/${team.id}?manage=true`);
                                                          return;
                                                        }
                                                      }

                                                      // If no team with pending requests found, navigate to first owned team
                                                      navigate(
                                                          `/teams/${ownedTeams[0].id}?manage=true`);
                                                    } catch (error) {
                                                      console.error(
                                                          'Error finding team with pending requests:',
                                                          error);
                                                      // Fallback to teams page
                                                      navigate('/teams');
                                                    }
                                                  } else {
                                                    // Fallback to teams page
                                                    navigate('/teams');
                                                  }
                                                }}
                                                className="bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 px-3 py-1 rounded-md text-sm font-semibold transition-colors"
                                            >
                                              {t('teams.manage',
                                                  {defaultValue: 'Manage'})}
                                            </button>
                                          </div>
                                        </div>
                                    )}

                                    {/* LFS Scrim Requests - Desktop */}
                                    {incomingRequests.filter(req => req.status === 'Proposed').length > 0 && (
                                        <div className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                          <div className="flex items-center justify-between">
                                            <div>
                                              <p className="font-semibold text-white">Scrim Requests</p>
                                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                                {incomingRequests.filter(req => req.status === 'Proposed').length} new scrim {incomingRequests.filter(req => req.status === 'Proposed').length === 1 ? 'request' : 'requests'}
                                              </p>
                                            </div>
                                            <button
                                              onClick={() => {
                                                setShowNotifications(false)
                                                navigate('/lfs/requests')
                                              }}
                                              className="bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 px-3 py-1 rounded-md text-sm font-semibold transition-colors"
                                            >
                                              View
                                            </button>
                                          </div>
                                        </div>
                                    )}

                                    {/* LFS Scrim Chat Notifications - Desktop */}
                                    {unreadScrimChats > 0 && (
                                      Object.entries(scrimNotifications).map(([scrimId, notification]) => (
                                        <div key={scrimId} className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                          <div className="flex items-center justify-between">
                                            <div>
                                              <p className="font-semibold text-white">Scrim Chat</p>
                                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                                New messages vs {notification.opponentTeam}
                                              </p>
                                            </div>
                                            <button
                                              onClick={() => {
                                                setShowNotifications(false)
                                                navigate(`/lfs/scrims?openChat=${scrimId}`)
                                              }}
                                              className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 transition-colors"
                                            >
                                              View
                                            </button>
                                          </div>
                                        </div>
                                      ))
                                    )}

                                    {/* LFS Request Notifications (Accept/Decline/Cancel) - Desktop */}
                                    {requestNotifications && requestNotifications.length > 0 && (
                                      requestNotifications.map(notification => (
                                        <div key={notification.id} className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                          <div className="flex items-center justify-between">
                                            <div className="flex-1 mr-4">
                                              <p className="font-semibold text-white">
                                                {notification.notification_type === 'request_accepted' && 'Request Accepted'}
                                                {notification.notification_type === 'request_expired' && 'Request Expired'}
                                                {notification.notification_type === 'request_cancelled' && 'Request Declined'}
                                              </p>
                                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                                {notification.notification_type === 'request_accepted' && `${notification.to_team_name} accepted your scrim request`}
                                                {notification.notification_type === 'request_expired' && `Your request to ${notification.to_team_name} has expired`}
                                                {notification.notification_type === 'request_cancelled' && `${notification.to_team_name} declined your scrim request`}
                                              </p>
                                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {notification.platform} • {notification.region} • {notification.format}
                                              </p>
                                            </div>
                                            <button
                                              onClick={async () => {
                                                // Determine team role for marking as read
                                                const { data: { user } } = await supabase.auth.getUser()
                                                if (!user) return

                                                const { data: userTeams } = await supabase
                                                  .from('team_members')
                                                  .select('team_id')
                                                  .eq('user_id', user.id)

                                                const teamIds = userTeams?.map(t => t.team_id) || []
                                                const isFromTeam = teamIds.includes(notification.from_team_id)
                                                const teamRole = isFromTeam ? 'from_team' : 'to_team'

                                                await markRequestNotificationRead(notification.id, teamRole)

                                                // Navigate to scrims page if request was accepted
                                                if (notification.notification_type === 'request_accepted') {
                                                  navigate('/lfs/scrims')
                                                }
                                              }}
                                              className={`px-3 py-1 rounded text-sm transition-colors ${
                                                notification.notification_type === 'request_accepted'
                                                  ? 'bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900'
                                                  : 'bg-[#22262f] border border-white/10 text-gray-300 hover:bg-[#262b35]'
                                              }`}
                                            >
                                              {notification.notification_type === 'request_accepted' ? 'View Scrim' : 'Dismiss'}
                                            </button>
                                          </div>
                                        </div>
                                      ))
                                    )}

                                    {/* LFS Scrim Cancellation Notifications - Desktop */}
                                    {scrimCancellationNotifications && scrimCancellationNotifications.length > 0 && (
                                      scrimCancellationNotifications.map(notification => (
                                        <div key={notification.id} className="p-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                          <div className="flex items-center justify-between">
                                            <div className="flex-1 mr-4">
                                              <p className="font-semibold text-white">
                                                Scrim Cancelled
                                              </p>
                                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                                {notification.cancelled_by_team_id === notification.team_a_id
                                                  ? `${notification.team_a_name} cancelled your scheduled scrim`
                                                  : `${notification.team_b_name} cancelled your scheduled scrim`
                                                }
                                              </p>
                                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {notification.platform} • {notification.region} • {notification.format}
                                              </p>
                                            </div>
                                            <button
                                              onClick={async () => {
                                                // Determine which team the user is on and mark as read
                                                const { data: { user } } = await supabase.auth.getUser()
                                                if (!user) return

                                                const { data: userTeams } = await supabase
                                                  .from('team_members')
                                                  .select('team_id')
                                                  .eq('user_id', user.id)

                                                const teamIds = userTeams?.map(t => t.team_id) || []
                                                await markScrimNotificationRead(notification.id, teamIds)
                                              }}
                                              className="bg-[#22262f] border border-white/10 text-gray-300 px-3 py-1 rounded-md text-sm font-semibold hover:bg-[#262b35] transition-colors"
                                            >
                                              Dismiss
                                            </button>
                                          </div>
                                        </div>
                                      ))
                                    )}

                                    {userInvites.length === 0
                                        && pendingJoinRequests === 0 && totalLFSNotifications === 0 && scheduleCount === 0 && tournamentSubmissionCount === 0 && (
                                            <div
                                                className="p-8 text-center text-gray-500 dark:text-gray-300">
                                              <Bell
                                                  className="w-8 h-8 mx-auto mb-2 opacity-50"/>
                                              <p>{t('common.noNotifications')}</p>
                                            </div>
                                        )}
                                  </div>
                                </div>
                              </>
                          )}
                        </div>

                        {/* Counter-Proposal Modal (schedule requests) */}
                        {showCounterProposalModal && selectedScheduleRequest && (
                          <CounterProposalModal
                            request={selectedScheduleRequest}
                            onSubmit={handleCounterProposalSubmit}
                            onClose={() => {
                              setShowCounterProposalModal(false);
                              setSelectedScheduleRequest(null);
                            }}
                          />
                        )}

                        {/* Your Teams Dropdown */}
                        <div className="relative teams-dropdown hidden sm:block order-1">
                          <button
                              onClick={() => setShowTeamsDropdown(!showTeamsDropdown)}
                              className="flex items-center justify-center p-2 text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 dark:hover:bg-white/30 rounded-lg touch-manipulation"
                              title={t('navigation.yourTeams', { defaultValue: 'Your Teams' })}
                              aria-label={t('navigation.yourTeams', { defaultValue: 'Your Teams' })}
                          >
                            <Users className="w-4 h-4"/>
                          </button>

                          {showTeamsDropdown && (
                              <div className="absolute right-0 mt-2 w-60 bg-[#12151e] rounded-xl shadow-2xl border border-white/10 overflow-hidden z-[9999]">
                                <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
                                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('navigation.yourTeams', { defaultValue: 'Your Teams' })}</span>
                                  <Link
                                      to="/teams"
                                      onClick={() => setShowTeamsDropdown(false)}
                                      className="text-xs text-gray-400 hover:text-white transition-colors no-underline"
                                  >
                                    {t('common.viewAll', { defaultValue: 'View all' })}
                                  </Link>
                                </div>
                                <div className="max-h-72 overflow-y-auto py-1">
                                  {userTeams && userTeams.length > 0 ? (
                                      userTeams.map((team) => (
                                          <Link
                                              key={team.id}
                                              to={`/teams/${team.id}`}
                                              onClick={() => setShowTeamsDropdown(false)}
                                              className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.06] transition-colors no-underline"
                                          >
                                            {team.logo ? (
                                                <img src={team.logo} alt={team.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0"/>
                                            ) : (
                                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400/30 to-teal-500/30 border border-white/10 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                                                  {(team.name || '?').charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                              <div className="text-sm font-semibold text-white truncate">{team.name}</div>
                                              {team.role && (
                                                  <div className="text-xs text-gray-400 capitalize">{team.role}</div>
                                              )}
                                            </div>
                                          </Link>
                                      ))
                                  ) : (
                                      <div className="px-4 py-6 text-center">
                                        <p className="text-sm text-gray-400 mb-3">{t('navigation.noTeamsYet', { defaultValue: "You're not on a team yet." })}</p>
                                      </div>
                                  )}
                                </div>
                                <Link
                                    to="/teams"
                                    onClick={() => setShowTeamsDropdown(false)}
                                    className="flex items-center gap-2 px-4 py-2.5 border-t border-white/10 text-sm font-medium text-white hover:bg-white/[0.06] transition-colors no-underline"
                                >
                                  <Plus className="w-4 h-4"/>
                                  {t('navigation.findOrCreateTeam', { defaultValue: 'Find or create a team' })}
                                </Link>
                              </div>
                          )}
                        </div>

                        {/* User Menu Dropdown */}
                        <div className="relative user-menu order-3">
                          <button
                              onClick={() => setShowUserMenu(!showUserMenu)}
                              className="flex items-center space-x-1 sm:space-x-2 cursor-pointer hover:bg-white/10 dark:hover:bg-white/20 rounded-lg p-1 sm:p-1.5 transition-all duration-300 group touch-manipulation"
                          >
                            <UserAvatar user={user} size="w-6 h-6 sm:w-7 sm:h-7" textSize="text-xs" showBorder={true} />
                            <div className="text-white hidden lg:flex items-center gap-1.5 text-left">
                              <span className="font-semibold text-sm leading-tight">{user?.displayName || user?.username}</span>
                              {user?.isAdmin && (
                                  <Crown className="w-3.5 h-3.5 text-yellow-300" title={t('common.admin')}/>
                              )}
                            </div>
                            <ChevronDown className={`w-3.5 h-3.5 text-white/60 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}/>
                          </button>

                          {showUserMenu && (
                              <div className="absolute right-0 mt-2 w-64 bg-[#12151e] rounded-xl shadow-2xl border border-white/10 overflow-hidden z-[9999]">
                                {/* Account header */}
                                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                                  <UserAvatar user={user} size="w-9 h-9" textSize="text-sm" />
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-white truncate">{user?.displayName || user?.username}</div>
                                    {user?.email && <div className="text-xs text-gray-400 truncate">{user.email}</div>}
                                  </div>
                                </div>
                                {/* Items */}
                                <div className="py-1">
                                  <Link
                                      to="/profile"
                                      onClick={() => setShowUserMenu(false)}
                                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-white hover:bg-white/[0.06] transition-colors no-underline"
                                  >
                                    <User className="w-4 h-4"/>
                                    {t('navigation.myProfile', { defaultValue: 'My Profile' })}
                                  </Link>
                                  {user?.isAdmin && (
                                      <Link
                                          to="/admin"
                                          onClick={() => setShowUserMenu(false)}
                                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-white hover:bg-white/[0.06] transition-colors no-underline"
                                      >
                                        <Shield className="w-4 h-4"/>
                                        {t('navigation.adminPanel', { defaultValue: 'Admin Panel' })}
                                      </Link>
                                  )}
                                </div>
                                <button
                                    onClick={() => { setShowUserMenu(false); handleLogout(); }}
                                    className="flex items-center gap-3 w-full px-4 py-2.5 border-t border-white/10 text-sm text-red-400 hover:bg-white/[0.06] transition-colors"
                                >
                                  <LogOut className="w-4 h-4"/>
                                  {t('navigation.signOut', { defaultValue: 'Sign out' })}
                                </button>
                              </div>
                          )}
                        </div>
                      </>
                  ) : (
                      /* Sign In Button for non-authenticated users */
                      <button
                          onClick={handleSignIn}
                          className="flex items-center gap-1.5 bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 transition-colors px-3 py-1.5 rounded-lg text-sm font-semibold"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                        </svg>
                        <span>{t('auth.signIn')}</span>
                      </button>
                  )}

                </div>
              </div>
            </div>
          </div>

          {/* Mobile Navigation - Only show on smaller screens */}
          <div
              className="lg:hidden bg-[#12151e] border-t border-white/10 safe-area-inset-bottom">
            <div className="mx-auto px-1">
              <div
                  className={`flex justify-between items-center py-2 scrollbar-hide ${
                    showMobileTournamentsDropdown 
                      ? 'overflow-visible' 
                      : 'overflow-x-auto'
                  } min-w-0`}>
                {/* Dashboard Button - Mobile */}
                {user && (
                    <Link
                        to="/"
                        className={`flex flex-col items-center justify-center space-y-1 px-2 py-2 rounded-lg transition-all duration-300 touch-manipulation min-h-[52px] flex-1 max-w-[60px] no-underline ${
                            location.pathname === '/'
                                ? 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                        }`}
                        title={t('navigation.home',
                            {defaultValue: 'Home'})}
                    >
                      <Home className="w-4 h-4"/>
                      <span
                          className="text-[9px] font-medium text-center leading-tight">HUB</span>
                    </Link>
                )}

                {mobileNavigationItems.map(item => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.key || (item.dropdown
                      && item.dropdown.some(
                          dropdownItem => dropdownItem.key === activeTab));
                  const isDisabled = !user && item.requiresAuth;

                  // Mobile-specific labels for small screens
                  const getMobileLabel = (key) => {
                    switch(key) {
                      case 'tournaments': return 'TOUR';
                      case 'teams': return 'TEAMS';
                      case 'players': return 'PLAYERS';
                      case 'stats': return 'STATS';
                      default: return item.label;
                    }
                  };

                  if (item.dropdown) {
                    // Determine which dropdown to show based on the item key
                    const isMobileDropdownOpen = showMobileTournamentsDropdown;
                    const setMobileDropdownOpen = setShowMobileTournamentsDropdown;
                    const mobileDropdownClass = 'mobile-tournaments-dropdown';
                    
                    return (
                        <div key={item.key}
                             className={`relative ${mobileDropdownClass}`}>
                          <button
                              onClick={() => {
                                if (isDisabled) {
                                  handleSignIn();
                                  return;
                                }
                                setMobileDropdownOpen(!isMobileDropdownOpen);
                              }}
                              className={`flex flex-col items-center justify-center space-y-1 px-2 py-2 rounded-lg transition-all duration-300 touch-manipulation min-h-[52px] flex-1 max-w-[60px] active:scale-95 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 ${
                                  isActive
                                      ? 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30'
                                      : isDisabled
                                          ? 'text-gray-400 dark:text-gray-500 cursor-pointer hover:text-gray-500 dark:hover:text-gray-400'
                                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                              }`}
                              title={isDisabled ? t('auth.signInRequired') : ''}
                          >
                            <div className="flex items-center space-x-0.5">
                              <Icon className={`w-4 h-4 ${isDisabled
                                  ? 'opacity-50' : ''}`}/>
                              {isMobileDropdownOpen ? (
                                  <ChevronUp className="w-2 h-2"/>
                              ) : (
                                  <ChevronDown className="w-2 h-2"/>
                              )}
                            </div>
                            <span
                                className={`text-[9px] font-medium ${isDisabled
                                    ? 'opacity-50'
                                    : ''} text-center leading-tight`}>{getMobileLabel(item.key)}</span>
                          </button>

                          {/* Mobile Dropdown Menu */}
                          {isMobileDropdownOpen && !isDisabled && (
                              <div 
                                className="fixed left-1/2 transform -translate-x-1/2 w-56 bg-[#12151e] rounded-lg shadow-2xl border border-white/10 py-2 max-h-64 overflow-y-auto animate-in slide-in-from-top-2 duration-200"
                                style={{
                                  top: 'calc(64px + 32px + 20px)',
                                  zIndex: 2147483647, // Maximum z-index value
                                  position: 'fixed'
                                }}
                              >
                                {item.dropdown.map(dropdownItem => {
                                  const DropdownIcon = dropdownItem.icon;
                                  return (
                                      <Link
                                          key={dropdownItem.key}
                                          to={dropdownItem.path}
                                          onClick={() => {
                                            setMobileDropdownOpen(false);
                                          }}
                                          className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium transition-all duration-200 touch-manipulation active:scale-95 focus:outline-none focus:bg-gray-100 dark:focus:bg-white/[0.08] no-underline ${
                                              activeTab === dropdownItem.key
                                                  ? 'bg-[#e6f9f3] dark:bg-[#174e42] text-[#38c494] dark:text-[#38c494]'
                                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.06]'
                                          }`}
                                      >
                                        <DropdownIcon className="w-4 h-4"/>
                                        <span>{dropdownItem.label}</span>
                                      </Link>
                                  );
                                })}
                              </div>
                          )}
                        </div>
                    );
                  }

                  // Regular navigation item
                  return (
                      <Link
                          key={item.key}
                          to={isDisabled ? '#' : item.path}
                          onClick={(e) => { if (isDisabled) { e.preventDefault(); handleSignIn(); } }}
                          className={`flex flex-col items-center justify-center space-y-1 px-2 py-2 rounded-lg transition-all duration-300 touch-manipulation min-h-[52px] flex-1 max-w-[60px] no-underline ${
                              isActive
                                  ? 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30'
                                  : isDisabled
                                      ? 'text-gray-400 dark:text-gray-500 cursor-pointer hover:text-gray-500 dark:hover:text-gray-400'
                                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                          }`}
                          title={isDisabled ? t('auth.signInRequired') : ''}
                      >
                        <Icon className={`w-4 h-4 ${isDisabled ? 'opacity-50'
                            : ''}`}/>
                        <span className={`text-[9px] font-medium ${isDisabled
                            ? 'opacity-50'
                            : ''} text-center leading-tight`}>{getMobileLabel(item.key)}</span>
                      </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Announcement Bar - Only show on dashboard */}
      {location.pathname === '/' && user && <AnnouncementBar />}

        {/* Main Content */}
        <main className="flex-1 transition-colors">{children}</main>

        {/* Ultra Clean Footer — edge-faded teal top line (matches the navbar) */}
        <footer className="relative"
                style={{background: '#12151e'}}>
          {/* Top accent line: bright in the middle, fades out at both edges */}
          <div aria-hidden className="pointer-events-none absolute top-0 inset-x-0 h-px z-20"
               style={{background: 'linear-gradient(to right, transparent 0%, rgba(49,197,144,0.85) 50%, transparent 100%)'}} />
          <div aria-hidden className="pointer-events-none absolute top-0 inset-x-0 h-[6px] z-10 blur-[3px]"
               style={{background: 'linear-gradient(to right, transparent 8%, rgba(49,197,148,0.35) 50%, transparent 92%)'}} />
          <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
            {/* Main Row: Logo + Language + Social */}
            <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 items-center">
              {/* Left: Logo + Wordmark + Copyright */}
              <div
                  className="flex items-center justify-center sm:justify-start gap-3 order-1 sm:order-1">
                <img
                    src="/logoirl.webp"
                    alt="VRG Logo"
                    className="w-9 h-9 sm:w-11 sm:h-11 object-contain"
                />
                <div className="text-center sm:text-left leading-tight">
                  <div className="font-bebas tracking-wide text-xl sm:text-2xl leading-none text-white">Virtual Rematch Gaming</div>
                  <div className="text-white/45 text-[11px] sm:text-xs mt-1">{t('footer.copyright')}</div>
                </div>
              </div>

              {/* Center: Language Switcher */}
              <div
                  className="flex justify-center order-3 sm:order-2 lg:order-2">
                <LanguageSwitcher/>
              </div>

              {/* Right: Connect With Us */}
              <div
                  className="flex flex-col sm:flex-row items-center justify-center lg:justify-end space-y-2 sm:space-y-0 sm:space-x-3 order-2 sm:order-3">
                <h4 className="text-white font-semibold text-xs sm:text-sm">{t(
                    'footer.connectWithUs')}</h4>
                <div className="flex items-center gap-1 sm:gap-2">
                  <a
                      href="https://discord.gg/AUHugKBQTd"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-white/70 transition-colors hover:border-[#31C590]/40 hover:bg-[#22262f] hover:text-[#23F8B5] touch-manipulation"
                  >
                    <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                        viewBox="0 0 24 24" fill="currentColor">
                      <path
                          d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
                    </svg>
                    <span className="text-xs font-semibold">Discord</span>
                  </a>

                  <a
                      href="https://x.com/virtrematchgam"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-white/70 transition-colors hover:border-[#31C590]/40 hover:bg-[#22262f] hover:text-[#23F8B5] touch-manipulation"
                  >
                    <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                        viewBox="0 0 24 24" fill="currentColor">
                      <path
                          d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <span className="text-xs font-semibold">Twitter</span>
                  </a>

                  <a
                      href="https://www.youtube.com/@VirtualRematchGaming"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-white/70 transition-colors hover:border-[#31C590]/40 hover:bg-[#22262f] hover:text-[#23F8B5] touch-manipulation"
                  >
                    <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                        viewBox="0 0 24 24" fill="currentColor">
                      <path
                          d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    <span className="text-xs font-semibold">YouTube</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Legal Links Row */}
            <div className="border-t border-white/10 mt-3 sm:mt-4 pt-3 sm:pt-4">
              <div
                  className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                <Link
                    to="/terms"
                    className="text-white/60 hover:text-white/90 text-xs transition-colors duration-300 touch-manipulation no-underline"
                >
                  {t('footer.termsOfService')}
                </Link>
                <span className="text-white/30">•</span>
                <Link
                    to="/privacy"
                    className="text-white/60 hover:text-white/90 text-xs transition-colors duration-300 touch-manipulation no-underline"
                >
                  {t('footer.privacyPolicy')}
                </Link>
                <span className="text-white/30">•</span>
                <a
                    href="https://virtualrematchgaming.featurebase.app/en"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/60 hover:text-white/90 text-xs transition-colors duration-300 touch-manipulation"
                >
                  {t('footer.reportBug')}
                </a>
              </div>
            </div>
          </div>
        </footer>

        {/* Chat Manager */}
        <ChatManager/>

        {/* Floating chat button (bottom-right) */}
        {user && (
          <button
            onClick={openChatHub}
            aria-label={t('common.chat', { defaultValue: 'Chat' })}
            title={t('common.chat', { defaultValue: 'Chat' })}
            className="fixed bottom-6 right-6 z-[9998] flex items-center justify-center w-14 h-14 rounded-full text-zinc-900 shadow-2xl hover:scale-105 active:scale-95 transition-transform"
            style={{ backgroundColor: '#31C590', boxShadow: '0 8px 30px rgba(49,197,144,0.4)' }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold border-2 border-[#12151e]">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        )}

        {/* Tournament Notifications */}
        {user && <TournamentNotifications/>}

        {/* Gaming profile setup (opened from the notifications bell) */}
        {showGamingSetup && (
          <PlatformSetupModal
            onClose={() => setShowGamingSetup(false)}
            onComplete={() => setShowGamingSetup(false)}
          />
        )}
      </div>
      );
};

export default Layout;
